import React from 'react';
import { CheckCircle2, ChevronRight, Stethoscope, Hospital, AlertCircle, HeartPulse } from 'lucide-react';
import { formatDateIST } from '../../utils/date';
import { MilestoneItem } from './ClinicalMilestoneTimeline';

const TYPE_META = {
  OP: { label: 'OP', cls: 'bg-blue-50 text-blue-800 border-blue-200', icon: Stethoscope },
  IP: { label: 'IP', cls: 'bg-purple-50 text-purple-800 border-purple-200', icon: Hospital },
  ER: { label: 'ER', cls: 'bg-red-50 text-red-800 border-red-200', icon: AlertCircle },
  CR: { label: 'CR', cls: 'bg-amber-50 text-amber-800 border-amber-200', icon: HeartPulse },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

/** One visit on the clinical milestone rail. */
export default function VisitMilestoneCard({ visit, index, isYou, onOpen, accent }) {
  const meta = TYPE_META[visit.visit_type] || TYPE_META.OP;
  const TypeIcon = meta.icon;
  const status = (visit.status || '').replace(/-/g, ' ');

  return (
    <MilestoneItem accent={accent} testId={`visit-milestone-${index}`}>
      <button
        type="button"
        onClick={onOpen}
        className="group w-full text-left"
        data-testid={`ext-visit-${index}`}
        data-appointment-id={visit.appointment_id || ''}
      >
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-[#071B34]">
            {visit.date ? formatDateIST(visit.date) : '—'}
            {visit.time ? ` · ${visit.time}` : ''}
          </p>
          <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${meta.cls}`}>
            <TypeIcon className="h-3 w-3" /> {meta.label}
          </span>
          {status ? (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-800">
              {status}
            </span>
          ) : null}
          {visit.consultation_completed ? (
            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
              <CheckCircle2 className="h-2.5 w-2.5" /> Notes
            </span>
          ) : null}
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[#5A6B7C]">
          <span className="inline-flex items-center gap-1 font-medium text-[#071B34]">
            <Stethoscope className="h-3 w-3" /> Dr. {visit.doctor_name || '—'}
            {isYou ? (
              <span className="ml-1 text-[9px] font-bold text-blue-700 bg-blue-50 px-1 rounded border border-blue-200">YOU</span>
            ) : null}
          </span>
          {visit.specialisation ? <span>{visit.specialisation}</span> : null}
          <span>{timeAgo(visit.date)}</span>
          {visit.scheme ? <span className="text-violet-700">{visit.scheme}</span> : null}
        </div>
        <div className="mt-2 flex justify-end">
          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#9AADBE] group-hover:text-[#F15A22]">
            Open details <ChevronRight className="h-3 w-3" />
          </span>
        </div>
      </button>
    </MilestoneItem>
  );
}
